/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */
import { useNavigate, useParams } from "@tanstack/react-router";
import type { Metric } from "@unbox/api";
import { ArrowLeft, BarChart3, Download, Edit3, Plus, Trash2 } from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import {
  type UpdateReportInput,
  useDeleteReport,
  useReport,
  useTemplateChartData,
  useUpdateReport,
} from "@/api/reports";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { useToast } from "@/hooks/use-toast";
import { useMerchant } from "@/providers/MerchantProvider";
import type { reportsDetailRoute } from "@/router";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { ChartReport, exportChartToPDF } from "./components/ChartReport";
import { ReportConfigurationModal } from "./components/ReportConfigurationModal";
import { exportToCSV, TableReport } from "./components/TableReport";
import { TemplateReportView } from "./components/TemplateReportView";

// Custom CSV export for template reports with non-standard metrics
const exportTemplateToCSV = (data: any[], metrics: string[], labels: Record<string, string>, reportName: string) => {
  if (!data || data.length === 0) {
    console.error("No data to export");
    return;
  }

  if (!metrics || metrics.length === 0) {
    console.error("No metrics specified");
    return;
  }

  // Format values for CSV
  const formatValueForCSV = (value: any) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return "";
    }
    return Number(value);
  };

  // For pattern-analysis, data is row-based (each row is a hook)
  // Headers: Hook, GMV, # Videos, Video %, # Views, View %
  const headers = ["Hook", ...metrics.map((m) => (labels && labels[m]) || m)];
  const csvRows = [headers.join(",")];

  // Add each data row
  data.forEach((row) => {
    const csvRow = [
      `"${row.dateLabel || ""}"`, // Hook name
      ...metrics.map((metric) => formatValueForCSV(row[metric])),
    ];
    csvRows.push(csvRow.join(","));
  });

  const csvContent = csvRows.join("\n");

  // Download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${reportName.replace(/[^a-z0-9]/gi, "_")}_report.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const ReportDetail: React.FC = () => {
  const { reportId } = useParams({ strict: false }) as ReturnType<(typeof reportsDetailRoute)["useParams"]>;
  const navigate = useNavigate();
  const merchant = useMerchant();

  const { data: report, isLoading: isLoadingReport, error } = useReport(reportId);

  // Fetch chart data separately for template reports
  const { data: chartData, isLoading: isLoadingChartData } = useTemplateChartData(
    report?.templateId ?? undefined,
    report?.startDate ?? undefined,
    report?.endDate ?? undefined,
  );

  // Edit modal state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const updateReport = useUpdateReport();
  const deleteReport = useDeleteReport();
  const { toast } = useToast();

  // Refs to get access to current data for export
  const tableDataRef = useRef<{ data: any[]; metrics: Metric[]; reportName: string } | null>(null);
  const chartElementRef = useRef<HTMLDivElement>(null);
  const templateDataRef = useRef<{ data: any; templateId: string; reportName: string } | null>(null);
  const templateChartElementRef = useRef<HTMLDivElement>(null);

  if (isLoadingReport || (report?.templateId && isLoadingChartData)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingIndicator label="Loading report..." size="lg" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BarChart3 className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Report not found</h3>
          <p className="text-sm text-gray-500 mb-4">The report you're looking for doesn't exist or has been deleted.</p>
          <Button onClick={() => navigate({ to: "/merchant/$merchantId/reports", params: { merchantId: merchant } })}>
            <div className="flex items-center justify-between">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Reports
            </div>
          </Button>
        </div>
      </div>
    );
  }

  const reportMetrics = Array.isArray(report.metrics) ? report.metrics : [];

  // Edit functionality handlers
  const handleEditReport = () => {
    setEditDialogOpen(true);
  };

  const handleUpdateReport = async (data: {
    name: string;
    layout: "chart" | "table" | "bar";
    metrics: Metric[];
    templateId?: string | null;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const reportInput: UpdateReportInput = {
        name: data.name,
        layout: data.layout,
        metrics: data.metrics,
        templateId: data.templateId,
        startDate: data.startDate,
        endDate: data.endDate,
      };

      await updateReport.mutateAsync({ id: report.id, input: reportInput });
      toast({ title: "Success", description: "Report updated successfully!" });

      setEditDialogOpen(false);
      return { id: report.id };
    } catch (error) {
      toast({ title: "Error", description: "Failed to update report. Please try again.", variant: "destructive" });
      return null;
    }
  };

  const handleDeleteReport = async () => {
    try {
      await deleteReport.mutateAsync(report.id);
      toast({
        title: `${report.layout === "chart" ? "Chart" : "Table"} deleted`,
        description: `"${report.name}" successfully deleted.`,
      });
      navigate({ to: "/merchant/$merchantId/reports", params: { merchantId: merchant } });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete report. Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="flex flex-col w-full h-screen gap-5 items-center">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-5 h-full px-16 pt-16 pb-8`}>
        {/* Header */}
        <div className="flex flex-row items-start justify-between flex-shrink-0">
          {/* Left Side */}
          <div className="flex flex-col">
            <h1 className="text-2xl font-normal text-primary flex flex-row items-center">{report.name}</h1>
            <Button
              color="tertiary"
              size="sm"
              onClick={() => navigate({ to: "/merchant/$merchantId/reports", params: { merchantId: merchant } })}
              className="text-gray-500 hover:text-gray-700 pl-0 mt-2 flex flex-row items-center justify-start gap-2"
            >
              <div className="flex items-center justify-between">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to all reports
              </div>
            </Button>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {false && (
              <Button disabled={true} color="secondary" size="sm" className="text-gray-600">
                <div className="flex items-center justify-between">
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Dashboard
                </div>
              </Button>
            )}
            <Button
              color="secondary"
              size="sm"
              className="text-gray-600"
              onClick={async () => {
                try {
                  // Check template reports first (they use different refs)
                  if (report.templateId) {
                    if (report.layout === "table" && templateDataRef.current?.data) {
                      // Template table layout → CSV export (custom format)
                      const exportData = templateDataRef.current.data;
                      exportTemplateToCSV(
                        exportData.data || [],
                        exportData.metrics || [],
                        exportData.labels || {},
                        templateDataRef.current.reportName || report.name,
                      );
                    } else if (
                      (report.layout === "chart" || report.layout === "bar") &&
                      templateChartElementRef.current
                    ) {
                      // Template chart/bar layout → PDF export
                      await exportChartToPDF(templateChartElementRef.current, report.name);
                    }
                  } else if (report.layout === "table" && tableDataRef.current) {
                    // Regular table layout → CSV export
                    exportToCSV(
                      tableDataRef.current.data,
                      tableDataRef.current.metrics,
                      tableDataRef.current.reportName,
                    );
                  } else if ((report.layout === "chart" || report.layout === "bar") && chartElementRef.current) {
                    // Regular chart/bar layout → PDF export
                    await exportChartToPDF(chartElementRef.current, report.name);
                  }
                } catch (error) {
                  console.error("Export failed:", error);
                  toast({
                    title: "Export failed",
                    description: "There was an error exporting the report. Please try again.",
                    variant: "destructive",
                  });
                }
              }}
            >
              <div className="flex items-center justify-between">
                <Download className="w-4 h-4 mr-2" />
                Export {report.layout === "table" ? "CSV" : "PDF"}
              </div>
            </Button>
            <Button
              color="secondary"
              size="sm"
              className="text-gray-600 hover:text-red-600"
              onClick={handleDeleteReport}
            >
              <div className="flex items-center justify-between">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete {report.layout === "chart" ? "Chart" : "Table"}
              </div>
            </Button>
            <Button color="secondary" size="sm" onClick={handleEditReport}>
              <div className="flex items-center justify-between">
                <Edit3 className="w-4 h-4 mr-2" />
                Edit {report.layout === "chart" ? "Chart" : "Table"}
              </div>
            </Button>
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1 min-h-0">
          {/* Template reports */}
          {report.templateId && chartData ? (
            <TemplateReportView
              templateId={report.templateId}
              chartData={chartData}
              reportName={report.name}
              startDate={new Date(report.startDate!)}
              endDate={new Date(report.endDate!)}
              createdAt={report.createdAt}
              updatedAt={report.updatedAt}
              onExport={(data, templateId, reportName) => {
                templateDataRef.current = { data, templateId, reportName };
              }}
              chartElementRef={templateChartElementRef}
            />
          ) : report.layout === "chart" ? (
            <div ref={chartElementRef}>
              <ChartReport
                metrics={reportMetrics}
                createdAt={report.createdAt}
                updatedAt={report.updatedAt}
                reportId={reportId}
                reportName={report.name}
                fixedStartDate={report.startDate ?? undefined}
                fixedEndDate={report.endDate ?? undefined}
              />
            </div>
          ) : (
            <TableReport
              metrics={reportMetrics}
              createdAt={report.createdAt}
              updatedAt={report.updatedAt}
              reportId={reportId}
              onExport={(data, metrics, reportName) => {
                // Store current table data for export
                tableDataRef.current = { data, metrics, reportName };
              }}
              reportName={report.name}
              fixedStartDate={report.startDate ?? undefined}
              fixedEndDate={report.endDate ?? undefined}
            />
          )}
        </div>

        {/* Edit Report Modal */}
        <ReportConfigurationModal
          isOpen={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSave={handleUpdateReport}
          isCreating={updateReport.isPending}
          editMode={true}
          existingReport={{
            id: report.id,
            name: report.name,
            layout: report.layout,
            metrics: reportMetrics,
            templateId: report.templateId,
            startDate: report.startDate,
            endDate: report.endDate,
          }}
        />
      </div>
    </div>
  );
};
